import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { useProductTour } from './useProductTour';
import { isOnboarded, isTourDone } from './onboarding';

/**
 * Dispara el recorrido automáticamente:
 *  - al llegar desde la bienvenida (location.state.startTour), o
 *  - si el usuario ya pasó por la bienvenida pero el tour quedó sin terminar.
 * No molesta a usuarios existentes (que nunca pasaron por la bienvenida); para
 * ellos el recorrido queda disponible bajo demanda en Configuración.
 * Montado dentro del AppShell para que existan las anclas del sidebar.
 */
export function TourLauncher(): null {
  const location = useLocation();
  const navigate = useNavigate();
  const tenantId = useAuthStore((s) => s.activeTenantId);
  const { start, ready } = useProductTour();
  const fired = useRef(false);

  const wantsStart = Boolean((location.state as { startTour?: boolean } | null)?.startTour);

  useEffect(() => {
    if (fired.current || !ready) return;
    const shouldStart = wantsStart || (isOnboarded(tenantId) && !isTourDone(tenantId));
    if (!shouldStart) return;
    fired.current = true;
    // Espera un tick a que el sidebar esté pintado antes de anclar los pasos.
    // El navigate (limpieza del flag) va DESPUÉS de start() dentro del callback:
    // si se hiciera antes, el re-render dispararía el cleanup del effect y
    // cancelaría este timeout antes de arrancar el tour.
    const t = window.setTimeout(() => {
      start();
      if (wantsStart) navigate(location.pathname, { replace: true, state: {} });
    }, 400);
    return () => window.clearTimeout(t);
  }, [ready, wantsStart, tenantId, start, navigate, location.pathname]);

  return null;
}
